import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { albumId: Number, userAlbumId: Number }

  connect() {
  }

  disconnect() {
  }

  async toggle(event) {
    if (event.target.closest(".sticker-controls")) return

    const card = event.currentTarget
    const qty = parseInt(card.dataset.stickerQuantity)

    if (qty === 0) {
      await this.markAndShowControls(card)
    } else if (qty === 1) {
      await this.removeAndHideControls(card)
    }
  }

  async markAndShowControls(card) {
    this.lastCard = card
    const stickerId = card.dataset.stickerId
    const data = await this.sendToggle(stickerId, "add")
    if (!data) return
    this.updateCard(card, data)
    this.showControls(card)
    this.updateProgress(data)
    this.updateCounts(data)
  }

  async removeAndHideControls(card) {
    this.lastCard = card
    const stickerId = card.dataset.stickerId
    const data = await this.sendToggle(stickerId, "remove")
    if (!data) return
    this.updateCard(card, data)
    this.removeControls(card)
    this.updateProgress(data)
    this.updateCounts(data)
  }

  showControls(card) {
    if (card.querySelector(".sticker-controls")) return

    const qty = parseInt(card.dataset.stickerQuantity)
    if (qty === 0) return

    const div = document.createElement("div")
    div.className = "sticker-controls"

    const minusBtn = document.createElement("button")
    minusBtn.className = "sticker-btn"
    minusBtn.textContent = "\u2212"
    minusBtn.dataset.action = "click->sticker#decrement"

    const qtyEl = document.createElement("span")
    qtyEl.className = "sticker-qty"
    qtyEl.textContent = qty

    const plusBtn = document.createElement("button")
    plusBtn.className = "sticker-btn"
    plusBtn.textContent = "+"
    plusBtn.dataset.action = "click->sticker#increment"

    div.appendChild(minusBtn)
    div.appendChild(qtyEl)
    div.appendChild(plusBtn)
    card.appendChild(div)
  }

  async increment(event) {
    event.stopPropagation()
    const card = event.currentTarget.closest(".sticker-card")
    if (!card) return
    this.lastCard = card
    const stickerId = card.dataset.stickerId
    const data = await this.sendToggle(stickerId, "add")
    if (!data) return
    this.updateCard(card, data)
    this.updateControlsQty(card, data.quantity)
    this.updateProgress(data)
    this.updateCounts(data)
  }

  async decrement(event) {
    event.stopPropagation()
    const card = event.currentTarget.closest(".sticker-card")
    if (!card) return
    this.lastCard = card
    const stickerId = card.dataset.stickerId
    const data = await this.sendToggle(stickerId, "remove")
    if (!data) return
    this.updateCard(card, data)
    if (data.quantity === 0) {
      this.removeControls(card)
    } else {
      this.updateControlsQty(card, data.quantity)
    }
    this.updateProgress(data)
    this.updateCounts(data)
  }

  sendToggle(stickerId, actionType) {
    return fetch(`/albums/${this.albumIdValue}/stickers/${stickerId}/toggle`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        "Accept": "application/json"
      },
      body: JSON.stringify({
        action_type: actionType,
        user_album_id: this.userAlbumIdValue
      })
    })
      .then(r => r.json())
      .catch(() => null)
  }

  updateCard(card, data) {
    card.dataset.stickerQuantity = data.quantity
    card.classList.remove("owned", "duplicate", "missing", "dup-2", "dup-3", "dup-4", "dup-5")

    if (data.quantity > 1) {
      card.classList.add("duplicate")
      const level = Math.min(data.quantity, 5)
      card.classList.add(`dup-${level}`)
    } else if (data.quantity === 1) {
      card.classList.add("owned")
    } else {
      card.classList.add("missing")
    }
  }

  updateControlsQty(card, qty) {
    const qtyEl = card.querySelector(".sticker-controls .sticker-qty")
    if (qtyEl) {
      qtyEl.textContent = qty
    }
  }

  removeControls(card) {
    const controls = card.querySelector(".sticker-controls")
    if (controls) {
      controls.remove()
    }
  }

  async batchToggle(event) {
    const section = this.element
    const cards = [...section.querySelectorAll(".sticker-card")]
    const hasMissing = cards.some(c => parseInt(c.dataset.stickerQuantity) === 0)

    const isDark = document.documentElement.classList.contains("dark")
    const confirmed = await window.Swal.fire({
      title: hasMissing ? "Adicionar todas?" : "Remover todas?",
      text: hasMissing
        ? "Todas as figurinhas desta seção serão marcadas"
        : "Todas as figurinhas desta seção serão removidas",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1e3a8a",
      background: isDark ? "#1f2937" : "#fff",
      color: isDark ? "#f3f4f6" : "#1c1917"
    })
    if (!confirmed.isConfirmed) return

    const promises = cards.map(card => {
      const qty = parseInt(card.dataset.stickerQuantity)
      if (hasMissing) {
        if (qty > 0) return Promise.resolve(null)
        return this.sendToggle(card.dataset.stickerId, "add")
      } else {
        return this.sendToggle(card.dataset.stickerId, "clear")
      }
    })

    const results = await Promise.all(promises)
    const last = results.filter(Boolean).pop()
    if (!last) return

    cards.forEach((card, i) => {
      const data = results[i]
      if (!data) return
      this.updateCard(card, data)
      if (data.quantity > 0) {
        this.showControls(card)
      } else {
        this.removeControls(card)
      }
    })

    this.updateProgress(last)
    this.updateCounts(last)

    const teamCounter = section.querySelector("[data-team-counter]")
    if (teamCounter) {
      const total = teamCounter.textContent.split("/")[1]
      const owned = section.querySelectorAll(".sticker-card.owned, .sticker-card.duplicate").length
      teamCounter.textContent = `${owned}/${total}`
    }
  }

  updateProgress(data) {
    document.querySelectorAll(".progress-bar-fill").forEach(bar => {
      bar.style.width = `${data.progress}%`
    })
  }

  updateCounts(data) {
    const section = this.lastCard?.closest("[data-team]")

    document.querySelectorAll("[data-count-owned]").forEach(el => {
      el.textContent = document.querySelectorAll(".sticker-card.owned, .sticker-card.duplicate").length
    })

    document.querySelectorAll("[data-count-duplicates]").forEach(el => {
      el.textContent = document.querySelectorAll(".sticker-card.duplicate").length
    })

    document.querySelectorAll("[data-count-missing]").forEach(el => {
      const total = document.querySelectorAll(".sticker-card").length
      const owned = document.querySelectorAll(".sticker-card.owned, .sticker-card.duplicate").length
      el.textContent = total - owned
    })

    if (section) {
      const teamCounter = section.querySelector("[data-team-counter]")
      if (teamCounter) {
        const total = section.querySelectorAll(".sticker-card").length
        const owned = section.querySelectorAll(".sticker-card.owned, .sticker-card.duplicate").length
        teamCounter.textContent = `${owned}/${total}`
      }
    }
  }
}
